// src/pages/IPTUAnual.js

import React, { useState, useEffect } from "react";
import "./CadastroImoveis.css";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Button,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ThemeProvider from "../theme/TemaMuiProvider";
import muiTheme from "../theme/muiTheme";
import { FaFilter, FaCopy, FaFileCsv, FaTrash, FaEye, FaEdit, FaTimes, FaSave, FaPaperclip } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";

// Helpers de máscara/decimal

function canonicalizeDigits(d) {
  if (!d) return "";
  d = d.replace(/^0+(?=\d)/, "");
  return d;
}

function computeNextDigits(prevDigits, newMasked) {
  let cand = (newMasked || "").replace(/\D/g, "");

  if (prevDigits && cand.length >= prevDigits.length + 1 && cand.startsWith(prevDigits) && /^[0]*$/.test(cand.slice(prevDigits.length, -1))) {
    return canonicalizeDigits(prevDigits + cand[cand.length - 1]);
  }

  return canonicalizeDigits(cand);
}

/**
 *Mantido para compatibilidade (não usa milhar).
 * Use formatMoneyFromDigits(d) para campos de dinheiro.
 */
function formatDecimalFromDigits(d) {
  if (!d) return "";
  if (d.length === 1) return `0,0${d}`;
  if (d.length === 2) return `0,${d}`;

  const inteiro = d.slice(0, -2);
  const decimal = d.slice(-2);

  return `${inteiro},${decimal}`;
}

function toDigits(raw) {
  if (!raw && raw !== 0) return "";
  const onlyDigits = raw.toString().replace(/\D/g, "");
  return canonicalizeDigits(onlyDigits);
}

function mascaraPercentual(v) {
  return (v || "").replace(/[^\d,]/g, "");
}

// =========================
// MÁSCARA METRAGEM (4 casas) - pt-BR
// =========================

function stripLeadingZeros(intDigits) {
  if (!intDigits) return "";
  const stripped = intDigits.replace(/^0+(?=\d)/, "");
  return stripped || "0";
}

function formatThousands(intDigits) {
  return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function maskMetragemTypingBR(input) {
  const s = String(input ?? "").trim();
  if (!s) return "";

  const cleaned = s.replace(/[^\d,]/g, "");
  const hasComma = cleaned.includes(",");

  const parts = cleaned.split(",");
  const rawInt = parts[0] ?? "";
  const rawDec = parts.slice(1).join("");

  let intDigits = rawInt.replace(/\D/g, "");
  intDigits = stripLeadingZeros(intDigits);

  if (!intDigits) return "";

  const intFormatted = formatThousands(intDigits);

  if (!hasComma) {
    return intFormatted;
  }

  const decDigits = rawDec.replace(/\D/g, "").slice(0, 4);

  return `${intFormatted},${decDigits}`;
}

function maskMetragem4BR(input) {
  const s = String(input ?? "").trim();
  if (!s) return "";

  const cleaned = s.replace(/[^\d,]/g, "");
  const hasComma = cleaned.includes(",");

  const parts = cleaned.split(",");
  const rawInt = parts[0] ?? "";
  const rawDec = parts.slice(1).join("");

  let intDigits = rawInt.replace(/\D/g, "");
  intDigits = stripLeadingZeros(intDigits);

  if (!intDigits) return "";

  const intFormatted = formatThousands(intDigits);

  if (!hasComma) {
    return `${intFormatted},0000`;
  }

  const decDigits = rawDec.replace(/\D/g, "").slice(0, 4).padEnd(4, "0");

  return `${intFormatted},${decDigits}`;
}

// =========================
// MÁSCARA DINHEIRO (com ponto de milhar) - a partir de dígitos (centavos)
// =========================
function formatMoneyFromDigits(d) {
  if (d === null || d === undefined || d === "") return "";

  const digits = String(d).replace(/\D/g, "");

  if (!digits) return "";

  if (digits.length === 1) return `0,0${digits}`;
  if (digits.length === 2) return `0,${digits}`;

  const inteiro = digits.slice(0, -2);
  const decimal = digits.slice(-2);

  const inteiroFmt = formatThousands(inteiro);
  return `${inteiroFmt},${decimal}`;
}

// =========================
// MÁSCARAS (CSV / EXCEL)
// =========================

function maskMetragemBR(value) {
  const s = String(value ?? "").trim();
  if (!s) return "";

  const normalized = s.replace(/\./g, "").replace(",", ".");
  const n = Number(normalized);

  if (!Number.isFinite(n)) return s;

  const fixed = n.toFixed(4);
  return fixed.replace(".", ",");
}

function maskMoney(value) {
  let v = String(value ?? "").replace(/\D/g, "");
  v = (Number(v) / 100).toFixed(2) + "";
  return v.replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function parseNumberLoose(v) {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;

  const s = String(v).trim();
  if (!s) return 0;

  if (s.includes(",")) {
    const normalized = s.replace(/\./g, "").replace(",", ".");
    const n = Number(normalized);
    return Number.isFinite(n) ? n : 0;
  }

  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function formatMoneyDisplay(v) {
  const n = parseNumberLoose(v);
  return numberToBrMoney(n);
}

// Helpers para cálculo

function onlyDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function brToNumber(v) {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v).trim();
  if (!s) return 0;
  const normalized = s.replace(/\./g, "").replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

function numberToBr(n) {
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2).replace(".", ",");
}

function numberToBrMoney(n) {
  if (!Number.isFinite(n)) return "";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);

  const [intPart, decPart] = abs.toFixed(2).split(".");
  const intFmt = formatThousands(intPart);

  return `${sign}${intFmt},${decPart}`;
}

function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// API helpers

function authHeaders() {
  const token = localStorage.getItem("token");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function jsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function apiGetAnosIptu() {
  const res = await fetch(`/api/iptu-anual/listar-anos`, {
    headers: authHeaders(),
  });
  const data = await jsonSafe(res);
  return { ok: res.ok, data, status: res.status };
}

async function apiGetIptuAno(ano) {
  const res = await fetch(`/api/iptu-anual/${ano}`, {
    headers: authHeaders(),
  });
  const data = await jsonSafe(res);
  return { ok: res.ok, data, status: res.status };
}

async function apiCriarIptuAno(ano, payload) {
  const res = await fetch(`/api/iptu-anual/${ano}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  });
  const data = await jsonSafe(res);
  return { ok: res.ok, data, status: res.status };
}

async function apiAtualizarIptuAno(ano, id, payload) {
  const res = await fetch(`/api/iptu-anual/${ano}/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  });
  const data = await jsonSafe(res);
  return { ok: res.ok, data, status: res.status };
}

async function apiExcluirIptuAno(ano, id) {
  const res = await fetch(`/api/iptu-anual/${ano}/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  const data = await jsonSafe(res);
  return { ok: res.ok, data, status: res.status };
}

async function apiGetImoveis() {
  const res = await fetch(`/api/imoveis`, {
    headers: authHeaders(),
  });
  const data = await jsonSafe(res);
  return { ok: res.ok, data, status: res.status };
}

async function apiGetIptuBase() {
  const res = await fetch(`/api/iptu`, {
    headers: authHeaders(),
  });
  const data = await jsonSafe(res);
  return { ok: res.ok, data, status: res.status };
}

function getRegistroId(item) {
  return item?.id ?? item?.iptuId ?? item?.imovelId ?? item?.iptu_id ?? item?.imovel_id ?? null;
}

function getRegistroCodigo(item) {
  return item?.codigo ?? item?.codigoBase ?? item?.codigo_base ?? item?.codigoIptu ?? item?.codigo_iptu ?? "";
}

export default function IPTUAnual() {
  const anoAtual = new Date().getFullYear();

  const [anoSelecionado, setAnoSelecionado] = useState("");
  const [anosDisponiveis, setAnosDisponiveis] = useState([]);

  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("iptuAnualGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("iptuAnualGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  const [buscaCodigoBase, setBuscaCodigoBase] = useState("");
  const [modalBuscarIptu, setModalBuscarIptu] = useState(false);
  const [filtroBuscaIptu, setFiltroBuscaIptu] = useState("");
  const [paginaBuscaIptu, setPaginaBuscaIptu] = useState(0);
  const [linhasPorPaginaBuscaIptu, setLinhasPorPaginaBuscaIptu] = useState(10);
  const [iptuBase, setIptuBase] = useState([]);
  const [enquadramentos, setEnquadramentos] = useState([]);
  const [statusLista, setStatusLista] = useState([]);

  const [form, setForm] = useState({
    anoIptu: String(anoAtual),
    iptuId: null,

    codigoBase: "",
    codigo: "",

    apelido: "",
    matriculaCodigo: "",
    status: "",

    areaTerreno: "",
    areaConstruida: "",
    enquadramento: "",
    aliquotaIptu: "",

    valorVenalTerreno: "",
    valorVenalTerrenoM2: "",
    valorVenalConstrucao: "",
    valorVenalConstrucaoM2: "",
    valorVenalImovel: "",

    impostoPredial: "",
    taxaColetaLixo: "",
    taxaLimpezaPublica: "",

    outrasAdicoes: "",
    descontoIptuVerde: "",
    outrosDescontos: "",

    valorIptuAno: "",
    valorIptuAnoAvista: "",
    descontoPgtoAvista: "",
    numeroParcelas: "12",
    valorIptuMes: "",
    prazoResponsabilidade: "",
    prazoResolutiva: "",
    observacoes: "",
  });

  const [digitsRaw, setDigitsRaw] = useState({
    areaTerreno: "",
    areaConstruida: "",

    valorVenalTerreno: "",
    valorVenalConstrucao: "",
    valorVenalImovel: "",

    taxaColetaLixo: "",
    taxaLimpezaPublica: "",

    outrasAdicoes: "",
    descontoIptuVerde: "",
    outrosDescontos: "",

    valorIptuAnoAvista: "",
  });

  const [editandoId, setEditandoId] = useState(null);

  const [modoVisualizacao, setModoVisualizacao] = useState(false);
  const [idVisualizando, setIdVisualizando] = useState(null);
  const [editandoIdForm, setEditandoIdForm] = useState(null);

  const formTravado = modoVisualizacao && !editandoIdForm;
  const podeMostrarExcluirNoForm = modoVisualizacao && !editandoIdForm && Boolean(idVisualizando);

  const [mostrarPendencias, setMostrarPendencias] = useState(false);

  const camposObrigatorios = [
    "anoIptu",
    "iptuId",
    "status",
    "apelido",
    "areaTerreno",
    "areaConstruida",
    "enquadramento",
    "aliquotaIptu",
    "valorVenalTerreno",
    "valorVenalConstrucao",
    "valorIptuAnoAvista",
    "numeroParcelas",
    "prazoResponsabilidade",
  ];

  const estaVazio = (v) => {
    if (v === null || v === undefined) return true;
    return String(v).trim() === "";
  };

  const camposNumericosObrigatorios = [
    "areaTerreno",
    "areaConstruida",
    "aliquotaIptu",
    "valorVenalTerreno",
    "valorVenalConstrucao",
    "taxaColetaLixo",
    "taxaLimpezaPublica",
    "outrasAdicoes",
    "descontoIptuVerde",
    "outrosDescontos",
    "valorIptuAnoAvista",
  ];

  const campoObrigatorioVazio = (campo) => {
    if (campo === "iptuId") return !form.iptuId;

    const valor = form[campo];

    // Campo vazio continua sendo inválido.
    if (estaVazio(valor)) return true;

    // Zero é um valor válido: 0, 0,00 e 0,0000.
    // Rejeitamos apenas valores que não representam um número válido.
    if (camposNumericosObrigatorios.includes(campo)) {
      const valorNormalizado = String(valor).trim().replace(/\./g, "").replace(",", ".");

      return !Number.isFinite(Number(valorNormalizado));
    }

    return false;
  };

  const classeCampoPendente = (campo) => {
    if (!mostrarPendencias) return "";
    if (!camposObrigatorios.includes(campo)) return "";

    return campoObrigatorioVazio(campo) ? "campo-pendente" : "";
  };

  const classeCampoFormulario = (campo, extra = "") => `${classeCampoPendente(campo)} campo-formulario-padrao ${extra}`.trim();

  const classeCampoReadonly = (extra = "") => `campo-formulario-padrao ${extra}`.trim();

  const erroCampoMUI = (campo) => {
    if (!mostrarPendencias) return false;
    if (!camposObrigatorios.includes(campo)) return false;

    return campoObrigatorioVazio(campo);
  };

  const muiCampoSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      display: "flex",
      alignItems: "center",

      "& fieldset": {
        borderColor: "var(--ig-b-555555)",
      },

      "&:hover fieldset": {
        borderColor: "var(--ig-b-777777)",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#2ea8ff",
        borderWidth: 2,
      },

      "&.Mui-error fieldset": {
        borderColor: "#ef4444 !important",
        borderWidth: 2,
      },

      "&.Mui-error:hover fieldset": {
        borderColor: "#ef4444 !important",
      },

      "&.Mui-error.Mui-focused fieldset": {
        borderColor: "#ef4444 !important",
        borderWidth: 2,
      },

      "&.Mui-disabled fieldset": {
        borderColor: "var(--ig-b-555555)",
      },
    },

    "& .MuiOutlinedInput-input": {
      padding: "16.5px 14px",
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "var(--ig-t-ffffff)",
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputLabel-root": {
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#2ea8ff",
    },

    "& .MuiInputLabel-root.Mui-error": {
      color: "#ef4444 !important",
    },

    "& .MuiInputLabel-root.Mui-disabled": {
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiSvgIcon-root": {
      color: "var(--ig-t-ffffff)",
    },
  };

  const menuPropsDark = {
    MenuProps: {
      PaperProps: {
        sx: {
          backgroundColor: "var(--ig-f-111827)",
          color: "var(--ig-t-ffffff)",

          "& .MuiMenuItem-root": {
            backgroundColor: "var(--ig-f-111827)",
            color: "var(--ig-t-ffffff)",
          },

          "& .MuiMenuItem-root:hover": {
            backgroundColor: "var(--ig-f-1f2937)",
          },

          "& .MuiMenuItem-root.Mui-selected": {
            backgroundColor: "var(--ig-f-1f2937) !important",
          },
        },
      },
    },
  };

  const [registros, setRegistros] = useState([]);
  const [imoveis, setImoveis] = useState([]);

  const [filtrados, setFiltrados] = useState([]);
  const [busca, setBusca] = useState("");
  const [carregando, setCarregando] = useState(true);

  // GED
  const [arquivos, setArquivos] = useState([]);
  const [modalGED, setModalGED] = useState(false);
  const [idSelecionadoGED, setIdSelecionadoGED] = useState(null);
  const [gedPendentes, setGedPendentes] = useState([]);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(5);
  const indexInicial = (paginaAtual - 1) * registrosPorPagina;
  const indexFinal = indexInicial + registrosPorPagina;
  const registrosPaginados = filtrados.slice(indexInicial, indexFinal);
  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / registrosPorPagina));

  const token = localStorage.getItem("token");
  let tipoUsuario = "";

  if (token) {
    try {
      const decoded = jwtDecode(token);
      tipoUsuario = decoded.tipo
        ?.toString()
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    } catch {}
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  const normalizarCodigo = (v) => (v || "").toString().trim().toUpperCase();
  const extrairBase = (v) => normalizarCodigo(v).split("/")[0];

  const apelidoInfo = form.apelido ?? "";

  const buscarRegistroIptuDoAnoPorCodigo = (codigoDigitado) => {
    const base = extrairBase(codigoDigitado);
    if (!base) return null;

    const reg = (registros || []).find((r) => {
      const b1 = extrairBase(r?.codigoBase);
      const b2 = extrairBase(r?.codigo);
      return b1 === base || b2 === base;
    });

    return reg || null;
  };

  const getNomeStatusById = (statusId) => {
    if (!statusId) return "";

    const st = (statusLista || []).find((s) => Number(s.id ?? s.status_id) === Number(statusId));

    return st?.nome ?? "";
  };

  function adicionarAnoDisponivel(ano) {
    setAnosDisponiveis((prev) => {
      const n = Number(ano);
      if (!Number.isInteger(n)) return prev;
      if (prev.includes(n)) return prev;
      return [...prev, n].sort((a, b) => a - b);
    });
  }

  const resolverIdIptuBase = (item) => {
    const raw = item?.id ?? item?.iptuId ?? item?.iptu_id ?? item?.imovelId ?? item?.imovel_id ?? item?.iptuBaseId ?? item?.iptu_base_id ?? null;

    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
  };

  const resolverCodigoIptuBase = (item) => {
    return normalizarCodigo(item?.codigo ?? item?.codigoBase ?? item?.codigo_base ?? item?.codigoIptu ?? item?.codigo_iptu ?? "");
  };

  const selecionarIptuBasePorCodigoBase = (codigoDigitado) => {
    const base = extrairBase(codigoDigitado);
    if (!base) return;

    const ip = (iptuBase || []).find((x) => resolverCodigoIptuBase(x) === base);
    if (!ip) return;

    const im = (imoveis || []).find((x) => normalizarCodigo(x?.codigo) === base);
    const idResolvido = resolverIdIptuBase(ip);

    setForm((f) => ({
      ...f,
      iptuId: idResolvido,
      apelido: ip.apelido || im?.apelido || f.apelido || "",
      matriculaCodigo: im?.codigo || "",
      codigoBase: resolverCodigoIptuBase(ip) || base,
      status: getNomeStatusById(ip.statusId ?? ip.status_id),
    }));
  };

  const selecionarImovelPorCodigoBase = (codigoDigitado) => {
    const base = extrairBase(codigoDigitado);
    if (!base) return;

    const im = (imoveis || []).find((x) => normalizarCodigo(x?.codigo) === base);
    if (!im) return;

    setForm((f) => ({
      ...f,
      apelido: im.apelido || f.apelido || "",
      matriculaCodigo: im.codigo || "",
      codigoBase: im.codigo || base,
    }));
  };

  const iptusDisponiveisParaBusca = (iptuBase || []).filter((item) => {
    const codigoBase = resolverCodigoIptuBase(item);
    if (!codigoBase) return false;

    const jaExisteNoAno = (registros || []).some((r) => {
      const baseExistente = extrairBase(r.codigo || r.codigoBase);
      return baseExistente === codigoBase;
    });

    return !jaExisteNoAno;
  });

  const iptusFiltradosBusca = iptusDisponiveisParaBusca.filter((item) => {
    const termo = filtroBuscaIptu.trim().toLowerCase();

    if (!termo) return true;

    const texto = [
      resolverCodigoIptuBase(item),
      item.apelido,
      item.inscricao,
      item.nomeProprietario,
      item.nomeProprietarioGrupo,
      item.endereco,
      item.bairro,
      item.cidade,
      item.estado,
      getNomeStatusById(item.statusId ?? item.status_id),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return texto.includes(termo);
  });

  const iptusPaginadosBusca = iptusFiltradosBusca.slice(
    paginaBuscaIptu * linhasPorPaginaBuscaIptu,
    paginaBuscaIptu * linhasPorPaginaBuscaIptu + linhasPorPaginaBuscaIptu,
  );

  const selecionarIptuModal = (item) => {
    const codigoBase = resolverCodigoIptuBase(item);
    if (!codigoBase) return;

    setBuscaCodigoBase(codigoBase);

    selecionarIptuBasePorCodigoBase(codigoBase);
    selecionarImovelPorCodigoBase(codigoBase);

    setForm((f) => ({
      ...f,
      codigoBase,
    }));

    setModalBuscarIptu(false);
    setFiltroBuscaIptu("");
    setPaginaBuscaIptu(0);
  };

  const handleBuscaCodigoBase = (e) => {
    if (formTravado) return;

    const v = normalizarCodigo(e.target.value);
    setBuscaCodigoBase(v);

    setForm((f) => ({ ...f, codigoBase: v }));

    const reg = buscarRegistroIptuDoAnoPorCodigo(v);
    if (reg) {
      visualizarRegistro(reg);
      return;
    }

    selecionarIptuBasePorCodigoBase(v);
    selecionarImovelPorCodigoBase(v);
  };

  // GED
  const visualizarArquivoGED = async (iptuAnualId, arquivoId, nomeArquivo = "") => {
    if (!iptuAnualId || !arquivoId) {
      alert("Arquivo inválido para visualização.");
      return;
    }

    const nome = (nomeArquivo || arquivoId || "").toLowerCase();

    const extensoesVisualizaveis = [".pdf", ".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp", ".svg"];

    const podeVisualizar = extensoesVisualizaveis.some((ext) => nome.endsWith(ext));

    if (!podeVisualizar) {
      const confirmar = window.confirm(
        `Visualização indisponível.\n\nO arquivo "${nomeArquivo}" não possui visualização integrada no ImobGest.\n\nPara visualizar este tipo de arquivo, é necessário baixar e abrir em um aplicativo compatível.\n\nDeseja baixar agora?`,
      );

      if (confirmar) {
        baixarArquivoGED(iptuAnualId, arquivoId, nomeArquivo);
      }

      return;
    }

    try {
      const resp = await fetch(`/api/iptu-anual/${iptuAnualId}/ged/${encodeURIComponent(arquivoId)}/visualizar`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) {
        throw new Error("Erro ao visualizar arquivo GED.");
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);

      window.open(url, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000);
    } catch (err) {
      console.error(err);
      alert("Erro ao visualizar arquivo GED.");
    }
  };

  const baixarArquivoGED = async (iptuAnualId, arquivoId, nome) => {
    try {
      const resp = await fetch(`/api/iptu-anual/${iptuAnualId}/ged/${encodeURIComponent(arquivoId)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) throw new Error("Falha no download");

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = nome || "arquivo";
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Erro ao baixar arquivo.");
    }
  };

  const excluirArquivoGED = async (iptuAnualId, arquivoId) => {
    if (!window.confirm("Deseja excluir este arquivo?")) return;

    try {
      const resp = await fetch(`/api/iptu-anual/${iptuAnualId}/ged/${encodeURIComponent(arquivoId)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) throw new Error("Erro ao excluir GED");

      setArquivos((old) => old.filter((a) => (a.id || a.nome) !== arquivoId));

      alert("Arquivo GED excluído!");
    } catch (err) {
      console.error(err);
      alert("Erro ao excluir arquivo GED.");
    }
  };

  const enviarGED = async (id, files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    try {
      const resp = await fetch(`/api/iptu-anual/${id}/ged`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });

      return resp.ok;
    } catch (err) {
      console.error("Erro ao enviar GED:", err);
      return false;
    }
  };

  const abrirGED = async (id) => {
    if (!id) return;

    setIdSelecionadoGED(id);
    setModalGED(true);

    try {
      const resp = await fetch(`/api/iptu-anual/${id}/ged`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const dados = await resp.json();
      setArquivos(Array.isArray(dados) ? dados : []);
    } catch (err) {
      console.error("Erro ao carregar arquivos GED:", err);
      setArquivos([]);
    }
  };

  async function carregarStatus() {
    try {
      const res = await fetch("/api/status", {
        headers: authHeaders(),
      });

      const data = await jsonSafe(res);
      const arr = res.ok && Array.isArray(data) ? data : [];
      setStatusLista(arr);
    } catch (e) {
      console.error("Erro ao carregar status:", e);
      setStatusLista([]);
    }
  }

  async function carregarAnosDisponiveis() {
    try {
      const { ok, data } = await apiGetAnosIptu();
      const arr = ok && Array.isArray(data) ? data.map(Number).filter(Number.isInteger) : [];
      setAnosDisponiveis(arr.sort((a, b) => a - b));
    } catch (e) {
      console.error("Erro ao carregar anos de IPTU:", e);
      setAnosDisponiveis([]);
    }
  }

  useEffect(() => {
    carregarAnosDisponiveis();
    carregarImoveis();
    carregarIptuBase();
    carregarEnquadramentos();
    carregarStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!anoSelecionado) {
      setRegistros([]);
      setFiltrados([]);
      setCarregando(false);
      return;
    }

    carregarIPTUDoAno(anoSelecionado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anoSelecionado]);

  useEffect(() => {
    const carregarArquivosDoForm = async () => {
      const idAtual = editandoId || idVisualizando;
      if (!idAtual) {
        setArquivos([]);
        return;
      }

      try {
        const resp = await fetch(`/api/iptu-anual/${idAtual}/ged`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const dados = await resp.json();
        setArquivos(Array.isArray(dados) ? dados : []);
      } catch {
        setArquivos([]);
      }
    };

    carregarArquivosDoForm();
  }, [editandoId, idVisualizando]);

  async function carregarIPTUDoAno(ano, manterPagina = false) {
    setCarregando(true);

    try {
      const { ok, data, status } = await apiGetIptuAno(ano);

      if (!ok) {
        console.error(`Erro HTTP ao listar IPTU ${ano}:`, {
          status,
          data,
        });
        throw new Error(data?.erro || data?.mensagem || `Falha ao buscar IPTU ${ano}`);
      }

      const arr = Array.isArray(data) ? data : [];

      setRegistros(arr);

      if (arr.length > 0) {
        adicionarAnoDisponivel(ano);
      }

      if (!busca.trim()) {
        setFiltrados(arr);
      } else {
        aplicarFiltro(busca, arr);
      }

      if (!manterPagina) {
        setPaginaAtual(1);
      }
    } catch (e) {
      console.error(`Erro ao listar IPTU ${ano}:`, e);
      setRegistros([]);
      setFiltrados([]);

      if (!manterPagina) {
        setPaginaAtual(1);
      }

      alert(e.message || `Erro ao carregar IPTU ${ano}.`);
    } finally {
      setCarregando(false);
    }
  }

  async function carregarImoveis() {
    try {
      const { ok, data } = await apiGetImoveis();
      const arr = ok && Array.isArray(data) ? data : [];
      setImoveis(arr);
    } catch (e) {
      console.error("Erro ao carregar imóveis:", e);
      setImoveis([]);
    }
  }

  async function carregarIptuBase() {
    try {
      const { ok, data } = await apiGetIptuBase();
      const arr = ok && Array.isArray(data) ? data : [];
      setIptuBase(arr);
    } catch (e) {
      console.error("Erro ao carregar IPTU base:", e);
      setIptuBase([]);
    }
  }

  async function carregarEnquadramentos() {
    try {
      const res = await fetch("/api/enquadramentos", {
        headers: authHeaders(),
      });

      const data = await jsonSafe(res);
      const arr = res.ok && Array.isArray(data) ? data : [];

      arr.sort((a, b) => String(a?.nome ?? "").localeCompare(String(b?.nome ?? "")));

      setEnquadramentos(arr);
    } catch (e) {
      console.error("Erro ao carregar enquadramentos:", e);
      setEnquadramentos([]);
    }
  }

  useEffect(() => {
    setForm((prev) => {
      const base = (prev.codigoBase || "").toString().trim();
      const ano = String(prev.anoIptu || "").trim();
      const codigoGerado = base && ano ? `${base}/${ano}` : "";

      if (prev.codigo === codigoGerado) return prev;
      return { ...prev, codigo: codigoGerado };
    });
  }, [form.anoIptu, form.codigoBase]);

  useEffect(() => {
    const areaTerreno = brToNumber(form.areaTerreno);
    const areaConstruida = brToNumber(form.areaConstruida);

    const aliquotaPct = brToNumber(form.aliquotaIptu);

    const vvTerreno = brToNumber(form.valorVenalTerreno);
    const vvConstrucao = brToNumber(form.valorVenalConstrucao);

    const vvImovel = round2(vvTerreno + vvConstrucao);

    const txLixo = brToNumber(form.taxaColetaLixo);
    const txLimpeza = brToNumber(form.taxaLimpezaPublica);
    const outrasAdicoes = brToNumber(form.outrasAdicoes);

    const descVerde = brToNumber(form.descontoIptuVerde);
    const outrosDesc = brToNumber(form.outrosDescontos);

    const iptuAvista = brToNumber(form.valorIptuAnoAvista);
    const parcelas = Number(form.numeroParcelas || 12) || 12;

    const vvTerrenoM2 = areaTerreno > 0 ? round2(vvTerreno / areaTerreno) : 0;
    const vvConstrucaoM2 = areaConstruida > 0 ? round2(vvConstrucao / areaConstruida) : 0;

    const impostoPredial = round2(vvImovel * (aliquotaPct / 100));

    const valorIptuAno = round2(impostoPredial + txLixo + txLimpeza + outrasAdicoes - descVerde - outrosDesc);

    const descontoPgtoAvistaPct = valorIptuAno > 0 ? round2((iptuAvista / valorIptuAno - 1) * 100) : 0;

    const valorIptuMes = parcelas > 0 ? round2(valorIptuAno / parcelas) : 0;

    setForm((prev) => {
      const next = {
        ...prev,

        valorVenalImovel: numberToBrMoney(vvImovel),

        valorVenalTerrenoM2: numberToBrMoney(vvTerrenoM2),
        valorVenalConstrucaoM2: numberToBrMoney(vvConstrucaoM2),

        impostoPredial: numberToBrMoney(impostoPredial),

        valorIptuAno: numberToBrMoney(valorIptuAno),

        valorIptuMes: numberToBrMoney(valorIptuMes),

        descontoPgtoAvista: numberToBr(descontoPgtoAvistaPct),
      };

      const same =
        next.valorVenalTerrenoM2 === prev.valorVenalTerrenoM2 &&
        next.valorVenalConstrucaoM2 === prev.valorVenalConstrucaoM2 &&
        next.impostoPredial === prev.impostoPredial &&
        next.valorIptuAno === prev.valorIptuAno &&
        next.descontoPgtoAvista === prev.descontoPgtoAvista &&
        next.valorIptuMes === prev.valorIptuMes;

      return same ? prev : next;
    });
  }, [
    form.areaTerreno,
    form.areaConstruida,
    form.aliquotaIptu,
    form.valorVenalTerreno,
    form.valorVenalConstrucao,
    form.taxaColetaLixo,
    form.taxaLimpezaPublica,
    form.outrasAdicoes,
    form.descontoIptuVerde,
    form.outrosDescontos,
    form.valorIptuAnoAvista,
    form.numeroParcelas,
  ]);

  const normalizar = (texto) =>
    texto
      ?.toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  function aplicarFiltro(texto, base) {
    const termos = texto
      .split(/[,.\s]+/)
      .map((t) => normalizar(t.trim()))
      .filter((t) => t.length > 0);

    const filtradosLocal = base.filter((item) => {
      const textoItem = normalizar(
        Object.values(item)
          .map((v) => (v == null ? "" : v.toString()))
          .join(" "),
      );
      return termos.every((termo) => textoItem.includes(termo));
    });

    setFiltrados(filtradosLocal);
  }

  const handleBusca = (e) => {
    const texto = e.target.value;
    setBusca(texto);

    if (!texto.trim()) {
      setFiltrados(registros);
      setPaginaAtual(1);
      return;
    }

    aplicarFiltro(texto, registros);
    setPaginaAtual(1);
  };

  const limparFiltro = () => {
    setBusca("");
    setFiltrados(registros);
    setPaginaAtual(1);
  };

  const copiarTabela = async () => {
    const texto = filtrados
      .map((r) => `${r.apelido}\t${r.areaTerreno}\t${r.areaConstruida}\t${r.valorVenalImovel}\t${r.valorIptuAno}\t${r.numeroParcelas}\t${r.valorIptuMes}`)
      .join("\n");

    const ok = await copiarTexto(texto);
    alert(ok ? "Registros copiados!" : "Não foi possível copiar.");
  };

  const exportarCSV = () => {
    const BOM = "\uFEFF";

    const header = [
      "Código",
      "Apelido",
      "Área Terreno (m²)",
      "Área Construída (m²)",
      "Enquadramento",
      "Alíquota IPTU",
      "Valor Venal Terreno",
      "Valor Venal Terreno (R$/m²)",
      "Valor Venal Construção",
      "Valor Venal Construção (R$/m²)",
      "Valor Venal Imóvel",
      "Imposto Predial",
      "Tx. Coleta de Lixo",
      "Tx. Limpeza Pública",
      "Outras Adições",
      "Desconto IPTU Verde",
      "Outros Descontos",
      "Valor IPTU Ano",
      "Valor IPTU Ano À Vista",
      "Desconto Pgto À Vista (%)",
      "Número de Parcelas",
      "Valor IPTU Mês",
      "Observações",
    ];

    const csvEscape = (value) => {
      const s = String(value ?? "");
      if (/[;"\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const excelText = (value) => `="${String(value ?? "")}"`;

    const linhas = (filtrados || []).map((r) =>
      [
        r.codigo ?? "",
        r.apelido ?? "",

        excelText(maskMetragemBR(r.areaTerreno ?? "")),
        excelText(maskMetragemBR(r.areaConstruida ?? "")),

        r.enquadramento ?? "",

        excelText(r.aliquotaIptu ?? ""),

        excelText(maskMoney(String(r.valorVenalTerreno ?? ""))),
        excelText(maskMoney(String(r.valorVenalTerrenoM2 ?? ""))),
        excelText(maskMoney(String(r.valorVenalConstrucao ?? ""))),
        excelText(maskMoney(String(r.valorVenalConstrucaoM2 ?? ""))),
        excelText(maskMoney(String(r.valorVenalImovel ?? ""))),
        excelText(maskMoney(String(r.impostoPredial ?? ""))),
        excelText(maskMoney(String(r.taxaColetaLixo ?? ""))),
        excelText(maskMoney(String(r.taxaLimpezaPublica ?? ""))),
        excelText(maskMoney(String(r.outrasAdicoes ?? ""))),
        excelText(maskMoney(String(r.descontoIptuVerde ?? ""))),
        excelText(maskMoney(String(r.outrosDescontos ?? ""))),
        excelText(maskMoney(String(r.valorIptuAno ?? ""))),
        excelText(maskMoney(String(r.valorIptuAnoAvista ?? ""))),

        excelText(String(r.descontoPgtoAvista ?? "")),
        excelText(String(r.numeroParcelas ?? "")),
        excelText(maskMoney(String(r.valorIptuMes ?? ""))),

        r.observacoes ?? "",
      ].map(csvEscape),
    );

    const csv = [header.map(csvEscape), ...linhas].map((l) => l.join(";")).join("\n");

    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `iptu_${anoSelecionado}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const limparFormulario = (anoParam = null) => {
    const anoFinal = anoParam ?? anoSelecionado ?? "";

    setEditandoId(null);
    setBuscaCodigoBase("");
    setModoVisualizacao(false);
    setIdVisualizando(null);
    setEditandoIdForm(null);
    setMostrarPendencias(false);
    setGedPendentes([]);
    setArquivos([]);
    setModalGED(false);
    setIdSelecionadoGED(null);

    setForm({
      anoIptu: String(anoFinal || ""),
      iptuId: null,
      codigoBase: "",
      codigo: "",
      apelido: "",
      matriculaCodigo: "",
      status: "",
      areaTerreno: "",
      areaConstruida: "",
      enquadramento: "",
      aliquotaIptu: "",
      valorVenalTerreno: "",
      valorVenalTerrenoM2: "",
      valorVenalConstrucao: "",
      valorVenalConstrucaoM2: "",
      valorVenalImovel: "",
      impostoPredial: "",
      taxaColetaLixo: "",
      taxaLimpezaPublica: "",
      outrasAdicoes: "",
      descontoIptuVerde: "",
      outrosDescontos: "",
      valorIptuAno: "",
      valorIptuAnoAvista: "",
      descontoPgtoAvista: "",
      numeroParcelas: "12",
      valorIptuMes: "",
      prazoResponsabilidade: "",
      observacoes: "",
      prazoResolutiva: "",
    });

    setDigitsRaw({
      areaTerreno: "",
      areaConstruida: "",
      valorVenalTerreno: "",
      valorVenalConstrucao: "",
      valorVenalImovel: "",
      taxaColetaLixo: "",
      taxaLimpezaPublica: "",
      outrasAdicoes: "",
      descontoIptuVerde: "",
      outrosDescontos: "",
      valorIptuAnoAvista: "",
    });
  };

  const carregarRegistroNoForm = (item) => {
    const baseFromItem = item?.codigoBase ?? extrairBase(item?.codigo) ?? item?.codigo ?? "" ?? "";
    const base = extrairBase(baseFromItem);

    const ip = (iptuBase || []).find((x) => resolverCodigoIptuBase(x) === normalizarCodigo(base));
    const im = (imoveis || []).find((x) => normalizarCodigo(x?.codigo) === normalizarCodigo(base));

    setBuscaCodigoBase(base || "");

    setForm({
      anoIptu: String(item.ano ?? anoSelecionado ?? anoAtual),
      iptuId: resolverIdIptuBase(ip) ?? item.iptuId ?? item.imovelId ?? null,
      codigoBase: base,
      codigo: item.codigo ?? "",

      apelido: item.apelidoBase || item.apelido || ip?.apelido || im?.apelido || "",
      matriculaCodigo: item.matriculaCodigo || im?.codigo || "",
      status: item.statusNome || getNomeStatusById(ip?.statusId ?? ip?.status_id),

      areaTerreno: maskMetragem4BR(item.areaTerreno ?? ""),
      areaConstruida: maskMetragem4BR(item.areaConstruida ?? ""),

      enquadramento: item.enquadramento ?? "",
      aliquotaIptu: item.aliquotaIptu ?? "",

      valorVenalTerreno: maskMoney(item.valorVenalTerreno ?? ""),
      valorVenalTerrenoM2: item.valorVenalTerrenoM2 ?? "",
      valorVenalConstrucao: maskMoney(item.valorVenalConstrucao ?? ""),
      valorVenalConstrucaoM2: item.valorVenalConstrucaoM2 ?? "",
      valorVenalImovel: maskMoney(item.valorVenalImovel ?? ""),

      impostoPredial: item.impostoPredial ?? "",
      taxaColetaLixo: maskMoney(item.taxaColetaLixo ?? ""),
      taxaLimpezaPublica: maskMoney(item.taxaLimpezaPublica ?? ""),

      outrasAdicoes: maskMoney(item.outrasAdicoes ?? ""),
      descontoIptuVerde: maskMoney(item.descontoIptuVerde ?? ""),
      outrosDescontos: maskMoney(item.outrosDescontos ?? ""),

      valorIptuAno: item.valorIptuAno ?? "",
      valorIptuAnoAvista: maskMoney(item.valorIptuAnoAvista ?? ""),
      descontoPgtoAvista: item.descontoPgtoAvista ?? "",

      numeroParcelas: (item.numeroParcelas ?? "12").toString(),
      valorIptuMes: item.valorIptuMes ?? "",
      prazoResponsabilidade: item.prazoResponsabilidade || item.prazo_responsabilidade || "",

      prazoResolutiva: onlyDate(item.prazoResolutiva || item.prazo_resolutiva || ""),

      observacoes: item.observacoes ?? "",
    });

    setDigitsRaw({
      areaTerreno: maskMetragem4BR(item.areaTerreno ?? ""),
      areaConstruida: maskMetragem4BR(item.areaConstruida ?? ""),

      valorVenalTerreno: toDigits(item.valorVenalTerreno ?? ""),
      valorVenalConstrucao: toDigits(item.valorVenalConstrucao ?? ""),
      valorVenalImovel: toDigits(item.valorVenalImovel ?? ""),

      taxaColetaLixo: toDigits(item.taxaColetaLixo ?? ""),
      taxaLimpezaPublica: toDigits(item.taxaLimpezaPublica ?? ""),
      outrasAdicoes: toDigits(item.outrasAdicoes ?? ""),
      descontoIptuVerde: toDigits(item.descontoIptuVerde ?? ""),
      outrosDescontos: toDigits(item.outrosDescontos ?? ""),

      valorIptuAnoAvista: toDigits(item.valorIptuAnoAvista ?? ""),
    });
  };

  const visualizarRegistro = (item) => {
    if (!podeVisualizar) return alert("Você não tem permissão para visualizar.");

    carregarRegistroNoForm(item);

    const anoDoItem = Number(item?.ano || anoSelecionado || anoAtual);
    if (Number.isInteger(anoDoItem)) {
      setAnoSelecionado(anoDoItem);
      adicionarAnoDisponivel(anoDoItem);
    }

    setIdVisualizando(getRegistroId(item));
    setEditandoId(null);
    setEditandoIdForm(null);
    setModoVisualizacao(true);

    setMostrarPendencias(true);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const entrarEdicao = () => {
    if (!podeEditar) return alert("Você não tem permissão para editar.");

    setEditandoId(idVisualizando);
    setEditandoIdForm(idVisualizando);
    setModoVisualizacao(false);

    setMostrarPendencias(true);
  };

  const excluirRegistro = async () => {
    if (!podeExcluir) return alert("Você não tem permissão para excluir.");
    if (!idVisualizando) return alert("Registro inválido.");

    if (!window.confirm(`Deseja excluir este registro de IPTU ${anoSelecionado}?`)) return;

    try {
      const { ok, data } = await apiExcluirIptuAno(anoSelecionado, idVisualizando);
      if (!ok) throw new Error(data?.erro || "Falha ao excluir");

      alert("Registro excluído!");
      limparFormulario(anoSelecionado);
      carregarIPTUDoAno(anoSelecionado);
    } catch (e) {
      console.error(e);
      alert(e?.message || "Erro ao excluir registro.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editandoId && !podeCadastrar) return alert("Você não tem permissão para cadastrar.");
    if (editandoId && !podeEditar) return alert("Você não tem permissão para editar.");

    setMostrarPendencias(true);

    const existeCampoObrigatorioVazio = camposObrigatorios.some((campo) => campoObrigatorioVazio(campo));

    if (existeCampoObrigatorioVazio) {
      alert("Preencha os campos obrigatórios destacados em vermelho.");
      return;
    }

    try {
      const ano = String(form.anoIptu || "").trim();
      const anoNumero = Number(ano);

      if (!Number.isInteger(anoNumero) || anoNumero < 2000 || anoNumero > 2100) {
        throw new Error("Informe um ano válido para o IPTU.");
      }

      let iptuId = form.iptuId;

      if (!iptuId) {
        const baseDigitada = extrairBase(form.codigoBase || buscaCodigoBase || "");

        if (baseDigitada) {
          const ip = (iptuBase || []).find((x) => resolverCodigoIptuBase(x) === baseDigitada);
          iptuId = resolverIdIptuBase(ip);
        }
      }

      if (!iptuId) {
        throw new Error("Selecione um IPTU base (ou busque pelo código ITxxx).");
      }

      let base = extrairBase((form.codigoBase || "").toString().trim());

      if (!base) {
        const ip = (iptuBase || []).find((x) => Number(resolverIdIptuBase(x)) === Number(iptuId));
        base = resolverCodigoIptuBase(ip) || "";
      }

      if (!base) throw new Error("IPTU (código base) é obrigatório.");

      const codigoGerado = `${base}/${ano}`;

      const payload = {
        ...form,
        prazoResolutiva: onlyDate(form.prazoResolutiva) || null,
        iptuId,
        codigoBase: base,
        codigo: codigoGerado,
      };

      delete payload.status;

      if (editandoId) {
        const { ok, data } = await apiAtualizarIptuAno(anoNumero, editandoId, payload);
        if (!ok) throw new Error(data?.erro || "Falha ao atualizar");

        alert(`IPTU ${anoNumero} atualizado com sucesso!`);

        setMostrarPendencias(true);
        adicionarAnoDisponivel(anoNumero);
        setAnoSelecionado(anoNumero);

        const paginaAnterior = paginaAtual;

        if (gedPendentes.length > 0) {
          const okGed = await enviarGED(editandoId, gedPendentes);

          if (okGed) {
            setGedPendentes([]);
            await carregarIPTUDoAno(anoNumero, true);
            setPaginaAtual(paginaAnterior);

            setIdVisualizando(editandoId);

            await abrirGED(editandoId);
            alert("Anexos GED pendentes enviados!");
          } else {
            await carregarIPTUDoAno(anoNumero, true);
            alert("IPTU atualizado, mas falhou ao enviar os anexos GED pendentes.");
          }
        } else {
          await carregarIPTUDoAno(anoNumero, true);
        }

        setIdVisualizando(editandoId);
        setEditandoId(null);
        setEditandoIdForm(null);
        setModoVisualizacao(true);
        return;
      }

      const { ok, data } = await apiCriarIptuAno(anoNumero, payload);
      if (!ok) throw new Error(data?.erro || "Falha ao cadastrar");

      alert(`IPTU ${anoNumero} cadastrado!`);

      setMostrarPendencias(true);
      adicionarAnoDisponivel(anoNumero);
      setAnoSelecionado(anoNumero);

      const novoId = getRegistroId(data);

      if (gedPendentes.length > 0 && novoId) {
        const okGed = await enviarGED(novoId, gedPendentes);

        if (okGed) {
          setGedPendentes([]);
          await carregarIPTUDoAno(anoNumero);
          await abrirGED(novoId);
          alert("Anexos GED pendentes enviados!");
        } else {
          await carregarIPTUDoAno(anoNumero);
          alert("IPTU cadastrado, mas falhou ao enviar os anexos GED pendentes.");
        }
      } else {
        await carregarIPTUDoAno(anoNumero);
      }

      limparFormulario(anoNumero);
    } catch (err) {
      console.error(`Erro ao salvar IPTU:`, err);
      alert(err?.message || `Erro ao salvar IPTU.`);
    }
  };

  const temGedNoForm =
    ((editandoId || idVisualizando) && arquivos.length > 0) ||
    gedPendentes.length > 0 ||
    (registros.find((r) => getRegistroId(r) === (editandoId || idVisualizando))?.qtdArquivos || 0) > 0;

  if (!podeVisualizar) {
    return (
      <div className="local-container">
        <h2>IPTU ANUAL</h2>
        <p className="aviso-permissao">🔒 Você não possui permissão para visualizar esta tela.</p>
      </div>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="local-container">
        <style>{`
        .campo-formulario-padrao,
        .campo-formulario-padrao:focus,
        .campo-formulario-padrao:active,
        .campo-formulario-padrao:read-only,
        .campo-formulario-padrao:disabled {
          background: transparent !important;
          background-color: transparent !important;
          color: var(--ig-t-ffffff) !important;
          box-shadow: none !important;
          -webkit-text-fill-color: var(--ig-t-ffffff) !important;
          opacity: 1 !important;
        }

        .campo-formulario-padrao::placeholder {
          color: var(--ig-t-255-255-255-a072) !important;
          -webkit-text-fill-color: var(--ig-t-255-255-255-a072) !important;
        }

        .campo-formulario-padrao::-webkit-calendar-picker-indicator {
  filter: var(--ig-x-11pezbt);
  opacity: 1 !important;
  cursor: pointer;
}
      `}</style>

        <h2>IPTU ANUAL</h2>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
          }}
        >
          <FormControl
            size="small"
            sx={{
              minWidth: 320,
              maxWidth: 360,

              "& .MuiOutlinedInput-root": {
                background: "var(--ig-f-1b2433)",
                color: "var(--ig-t-ffffff)",
                borderRadius: "10px",

                "& fieldset": {
                  borderColor: "var(--ig-b-314158)",
                },

                "&:hover fieldset": {
                  borderColor: "#2ea8ff",
                },

                "&.Mui-focused fieldset": {
                  borderColor: "#2ea8ff",
                },
              },

              "& .MuiInputLabel-root": {
                color: "var(--ig-t-c9d4e5)",
              },

              "& .MuiInputLabel-root.Mui-focused": {
                color: "#2ea8ff",
              },

              "& .MuiSvgIcon-root": {
                color: "var(--ig-t-ffffff)",
              },

              '& input[type="date"]::-webkit-calendar-picker-indicator': {
                filter: "var(--ig-x-11r8gad)",
                opacity: 1,
                cursor: "pointer",
              },

              '& input[type="month"]::-webkit-calendar-picker-indicator': {
                filter: "var(--ig-x-11r8gad)",
                opacity: 1,
                cursor: "pointer",
              },
            }}
          >
            <InputLabel>Buscar Ano</InputLabel>

            <Select
              value={anoSelecionado}
              label="Buscar Ano"
              onChange={(e) => {
                const valor = e.target.value;

                setBusca("");
                setPaginaAtual(1);

                if (!valor) {
                  setAnoSelecionado("");
                  setRegistros([]);
                  setFiltrados([]);
                  limparFormulario("");
                  return;
                }

                const ano = Number(valor);

                setAnoSelecionado(ano);
                limparFormulario(ano);
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: "var(--ig-f-1b2433)",
                    color: "var(--ig-t-ffffff)",

                    "& .MuiMenuItem-root": {
                      minHeight: 42,
                    },

                    "& .MuiMenuItem-root:hover": {
                      bgcolor: "#27364d",
                    },

                    "& .Mui-selected": {
                      bgcolor: "#0d6efd !important",
                    },
                  },
                },
              }}
            >
              <MenuItem value="">Selecione...</MenuItem>

              {anosDisponiveis.map((ano) => (
                <MenuItem key={ano} value={ano}>
                  IPTU {ano}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {anoSelecionado && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2,
              width: "100%",
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder={`Filtrar IPTU ${anoSelecionado}`}
              value={busca}
              onChange={handleBusca}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <FaFilter style={{ color: "var(--ig-t-94a3b8)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 60,
                  backgroundColor: "#0f172a",
                  color: "#fff",
                  borderRadius: "4px",
                  fontSize: "1rem",

                  "& fieldset": {
                    borderColor: "var(--ig-b-374151)",
                  },

                  "&:hover fieldset": {
                    borderColor: "#2ea8ff",
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: "#2ea8ff",
                  },
                },

                "& .MuiInputBase-input::placeholder": {
                  color: "var(--ig-t-94a3b8)",
                  opacity: 1,
                },
              }}
            />

            <Button
              variant="outlined"
              onClick={limparFiltro}
              sx={{
                height: 60,
                minWidth: 130,
                color: "#2ea8ff",
                borderColor: "#2ea8ff",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "4px",
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              variant="contained"
              startIcon={<FaCopy />}
              onClick={copiarTabela}
              sx={{
                height: 60,
                minWidth: 100,
                backgroundColor: "#2196f3",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "4px",
              }}
            >
              Copiar
            </Button>

            <Button
              variant="contained"
              onClick={exportarCSV}
              sx={{
                height: 60,
                minWidth: 80,
                backgroundColor: "#2196f3",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "4px",
              }}
            >
              CSV
            </Button>
          </Box>
        )}

        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button type="button" className="btn-grid-toggle small danger" onClick={() => setGridMinimizado(true)} title="Minimizar o Grid">
              ↘ Minimizar
            </button>
          ) : (
            <button type="button" className="btn-grid-toggle small outline" onClick={() => setGridMinimizado(false)} title="Expandir o Grid">
              ↗ Expandir
            </button>
          )}
        </div>

        {!anoSelecionado && (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">📅 Selecione um ano para visualizar os registros de IPTU.</p>
          </div>
        )}

        {anoSelecionado && !gridMinimizado && (
          <div className="bloco-lista">
            {carregando ? (
              <p>Carregando...</p>
            ) : filtrados.length === 0 ? (
              <p>Nenhum registro de IPTU {anoSelecionado} encontrado</p>
            ) : (
              <>
                <div className="tabela-scroll">
                  <table className="tabela-iptu-anual">
                    <colgroup>
                      <col style={{ width: "110px" }} />
                      <col style={{ width: "130px" }} />
                      <col style={{ width: "115px" }} />
                      <col style={{ width: "125px" }} />
                      <col style={{ width: "120px" }} />
                      <col style={{ width: "120px" }} />
                      <col style={{ width: "110px" }} />
                      <col style={{ width: "240px" }} />
                    </colgroup>

                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Apelido</th>
                        <th>VV Terreno</th>
                        <th>VV Construção</th>
                        <th>IPTU Ano</th>
                        <th>IPTU À Vista</th>
                        <th>IPTU Mês</th>
                        <th>Ações</th>
                      </tr>
                    </thead>

                    <tbody>
                      {registrosPaginados.map((item, index) => (
                        <tr key={getRegistroId(item) ?? `${getRegistroCodigo(item)}-${index}`}>
                          <td>{item.codigo}</td>
                          <td>{item.apelido}</td>
                          <td>{formatMoneyDisplay(item.valorVenalTerrenoM2)}</td>
                          <td>{formatMoneyDisplay(item.valorVenalConstrucaoM2)}</td>
                          <td>{formatMoneyDisplay(item.valorIptuAno)}</td>
                          <td>{formatMoneyDisplay(item.valorIptuAnoAvista)}</td>
                          <td>{formatMoneyDisplay(item.valorIptuMes)}</td>

                          <td
                            className="col-acoes"
                            style={{
                              gap: "8px",
                              justifyContent: "flex-start",
                            }}
                          >
                            <button
                              className={`btn-ged-lista ${item.qtdArquivos > 0 ? "btn-ged-ok" : ""}`}
                              type="button"
                              onClick={() => getRegistroId(item) && abrirGED(getRegistroId(item))}
                            >
                              GED
                              {item.qtdArquivos > 0 && <span className="ged-badge">{item.qtdArquivos}</span>}
                            </button>

                            {podeVisualizar && (
                              <button className="btn-editar btn-visualizar-lista" onClick={() => visualizarRegistro(item)} type="button">
                                <FaEye /> Visualizar
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 3,
                    minHeight: 42,
                    color: "var(--ig-t-ffffff)",
                    fontSize: "0.8rem",
                    mt: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>IPTU {anoSelecionado} por página:</span>

                    <Select
                      size="small"
                      value={registrosPorPagina}
                      onChange={(e) => {
                        setRegistrosPorPagina(Number(e.target.value));
                        setPaginaAtual(1);
                      }}
                      sx={{
                        height: 32,
                        color: "var(--ig-t-ffffff)",
                        fontSize: "0.8rem",
                        ".MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        ".MuiSvgIcon-root": {
                          color: "var(--ig-t-ffffff)",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            backgroundColor: "var(--ig-f-111827)",
                            color: "var(--ig-t-ffffff)",
                            border: "1px solid var(--ig-b-334155)",

                            "& .MuiMenuItem-root": {
                              color: "var(--ig-t-ffffff)",
                              backgroundColor: "var(--ig-f-111827)",
                            },

                            "& .MuiMenuItem-root:hover": {
                              backgroundColor: "var(--ig-f-1e293b)",
                            },

                            "& .Mui-selected": {
                              backgroundColor: "#334155 !important",
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                    </Select>
                  </Box>

                  <span>{filtrados.length === 0 ? "0–0 de 0" : `${indexInicial + 1}–${Math.min(indexFinal, filtrados.length)} de ${filtrados.length}`}</span>

                  <Button
                    type="button"
                    disabled={paginaAtual <= 1}
                    onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
                    sx={{
                      minWidth: 32,
                      color: "var(--ig-t-ffffff)",
                      fontSize: "1.4rem",
                      opacity: paginaAtual <= 1 ? 0.35 : 1,
                    }}
                  >
                    ‹
                  </Button>

                  <Button
                    type="button"
                    disabled={paginaAtual >= totalPaginas}
                    onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))}
                    sx={{
                      minWidth: 32,
                      color: "var(--ig-t-ffffff)",
                      fontSize: "1.4rem",
                      opacity: paginaAtual >= totalPaginas ? 0.35 : 1,
                    }}
                  >
                    ›
                  </Button>
                </Box>
              </>
            )}
          </div>
        )}

        {/* MODAL GED */}
        {modalGED && (
          <div className="modal-ged-overlay">
            <div className="modal-ged">
              <h3>Arquivos GED - IPTU ANUAL #{idSelecionadoGED}</h3>

              {arquivos.length === 0 ? (
                <p>Nenhum arquivo cadastrado para este IPTU anual.</p>
              ) : (
                <ul style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {arquivos.map((arq) => {
                    const arquivoId = arq.id || arq.nome;

                    return (
                      <li key={arquivoId} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ flex: 1 }}>{arq.nome}</span>

                        <button type="button" className="btn-visualizar-ged" onClick={() => visualizarArquivoGED(idSelecionadoGED, arquivoId, arq.nome)}>
                          <FaEye /> Visualizar
                        </button>

                        <button type="button" className="btn-baixar-ged" onClick={() => baixarArquivoGED(idSelecionadoGED, arquivoId, arq.nome)}>
                          Baixar
                        </button>

                        <button type="button" className="btn-excluir-ged" onClick={() => excluirArquivoGED(idSelecionadoGED, arquivoId)}>
                          <FaTrash /> Excluir
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              <button type="button" className="btn-fechar-ged" onClick={() => setModalGED(false)}>
                Fechar
              </button>
            </div>
          </div>
        )}

        <Dialog
          open={modalBuscarIptu}
          onClose={() => setModalBuscarIptu(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "var(--ig-f-2c2c2c)",
              color: "var(--ig-t-ffffff)",
              backgroundImage: "none",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "var(--ig-t-ffffff)",
              fontWeight: 700,
            }}
          >
            Selecionar IPTU Base - Ano {form.anoIptu || anoSelecionado || ""}
            <IconButton onClick={() => setModalBuscarIptu(false)}>
              <CloseIcon sx={{ color: "var(--ig-t-aaaaaa)" }} />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                value={filtroBuscaIptu}
                onChange={(e) => {
                  setFiltroBuscaIptu(e.target.value);
                  setPaginaBuscaIptu(0);
                }}
                placeholder="Código, apelido, proprietário, endereço, bairro ou cidade..."
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 56,

                    "& fieldset": {
                      borderColor: "var(--ig-b-555555)",
                    },

                    "&:hover fieldset": {
                      borderColor: "var(--ig-b-777777)",
                    },

                    "&.Mui-focused fieldset": {
                      borderColor: "#2ea8ff",
                      borderWidth: 2,
                    },
                  },

                  "& .MuiOutlinedInput-input": {
                    color: "var(--ig-t-ffffff)",
                  },

                  "& .MuiInputBase-input::placeholder": {
                    color: "var(--ig-t-aaaaaa)",
                    opacity: 1,
                  },
                }}
              />

              <Button variant="contained" sx={{ minWidth: 110 }}>
                Pesquisar
              </Button>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Apelido</TableCell>
                  <TableCell>Inscrição</TableCell>
                  <TableCell>Proprietário</TableCell>
                  <TableCell>Cidade/UF</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {iptusPaginadosBusca.map((item) => (
                  <TableRow key={item.id || resolverCodigoIptuBase(item)} hover onClick={() => selecionarIptuModal(item)} sx={{ cursor: "pointer" }}>
                    <TableCell>{resolverCodigoIptuBase(item)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{item.apelido || ""}</TableCell>
                    <TableCell>{item.inscricao || ""}</TableCell>
                    <TableCell>{item.nomeProprietario || item.nomeProprietarioGrupo || ""}</TableCell>
                    <TableCell>{[item.cidade, item.estado || item.uf].filter(Boolean).join("/")}</TableCell>
                    <TableCell>{getNomeStatusById(item.statusId ?? item.status_id) || item.status || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={iptusFiltradosBusca.length}
              page={paginaBuscaIptu}
              onPageChange={(_, novaPagina) => setPaginaBuscaIptu(novaPagina)}
              rowsPerPage={linhasPorPaginaBuscaIptu}
              onRowsPerPageChange={(e) => {
                setLinhasPorPaginaBuscaIptu(parseInt(e.target.value, 10));
                setPaginaBuscaIptu(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{
                color: "var(--ig-t-ffffff)",
                "& .MuiTablePagination-selectIcon": {
                  color: "var(--ig-t-ffffff)",
                },
                "& .MuiTablePagination-actions button": {
                  color: "var(--ig-t-ffffff)",
                },
              }}
            />
          </DialogContent>
        </Dialog>

        {podeCadastrar ? (
          <div className="bloco-formulario">
            <form className="form-grid" onSubmit={handleSubmit}>
              <div className="form-row">
                <TextField
                  label="Ano do IPTU*"
                  type="number"
                  value={form.anoIptu ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("anoIptu")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  inputProps={{ min: 2000, max: 2100 }}
                  onChange={(e) => {
                    if (formTravado) return;
                    setForm((f) => ({
                      ...f,
                      anoIptu: e.target.value.replace(/\D/g, "").slice(0, 4),
                    }));
                  }}
                />

                <TextField
                  label="Buscar Código IPTU*"
                  value={buscaCodigoBase}
                  onClick={() => {
                    if (formTravado) return;

                    if (!form.anoIptu) {
                      alert("Informe primeiro o Ano do IPTU.");
                      return;
                    }

                    setModalBuscarIptu(true);
                  }}
                  disabled={formTravado}
                  error={erroCampoMUI("iptuId")}
                  fullWidth
                  size="small"
                  sx={{
                    ...muiCampoSx,
                    cursor: "pointer",
                    "& .MuiInputBase-input": {
                      cursor: "pointer",
                      color: "var(--ig-t-ffffff)",
                    },
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: <SearchIcon sx={{ color: "var(--ig-t-ffffff)" }} />,
                  }}
                  placeholder="Clique para selecionar o IPTU"
                />

                <TextField label="Apelido*" value={apelidoInfo} disabled error={erroCampoMUI("apelido")} fullWidth size="small" sx={muiCampoSx} />

                <TextField label="Status" value={form.status ?? ""} disabled fullWidth size="small" sx={muiCampoSx} />

                <TextField label="Código Anual" value={form.codigo ?? ""} disabled fullWidth size="small" sx={muiCampoSx} />
              </div>

              <div className="form-row">
                <TextField
                  label="Área Terreno*"
                  value={form.areaTerreno ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("areaTerreno")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formTravado) return;
                    setForm((f) => ({
                      ...f,
                      areaTerreno: maskMetragemTypingBR(e.target.value),
                    }));
                  }}
                  onBlur={() => {
                    if (formTravado) return;
                    setForm((f) => ({
                      ...f,
                      areaTerreno: maskMetragem4BR(f.areaTerreno),
                    }));
                  }}
                />

                <TextField
                  label="Área Construída*"
                  value={form.areaConstruida ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("areaConstruida")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formTravado) return;
                    setForm((f) => ({
                      ...f,
                      areaConstruida: maskMetragemTypingBR(e.target.value),
                    }));
                  }}
                  onBlur={() => {
                    if (formTravado) return;
                    setForm((f) => ({
                      ...f,
                      areaConstruida: maskMetragem4BR(f.areaConstruida),
                    }));
                  }}
                />

                <TextField
                  select
                  label="Enquadramento*"
                  value={form.enquadramento ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("enquadramento")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  slotProps={{
                    select: menuPropsDark,
                  }}
                  onChange={(e) => {
                    if (formTravado) return;
                    setForm((f) => ({
                      ...f,
                      enquadramento: e.target.value,
                    }));
                  }}
                >
                  <MenuItem value="">Selecione o enquadramento</MenuItem>

                  {(enquadramentos || []).map((item) => (
                    <MenuItem key={item.id ?? item.nome} value={item.nome ?? ""}>
                      {item.nome ?? ""}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Alíquota IPTU (%)*"
                  value={form.aliquotaIptu ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("aliquotaIptu")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formTravado) return;
                    setForm((f) => ({
                      ...f,
                      aliquotaIptu: mascaraPercentual(e.target.value),
                    }));
                  }}
                />
              </div>

              <div className="form-row">
                <TextField
                  label="Valor Venal Terreno*"
                  value={form.valorVenalTerreno ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("valorVenalTerreno")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formTravado) return;
                    setDigitsRaw((prev) => {
                      const d = computeNextDigits(prev.valorVenalTerreno, e.target.value);
                      setForm((f) => ({ ...f, valorVenalTerreno: formatMoneyFromDigits(d) }));
                      return { ...prev, valorVenalTerreno: d };
                    });
                  }}
                />

                <TextField label="Valor Venal Terreno (R$/m²)" value={form.valorVenalTerrenoM2 ?? ""} disabled fullWidth size="small" sx={muiCampoSx} />

                <TextField
                  label="Valor Venal Construção*"
                  value={form.valorVenalConstrucao ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("valorVenalConstrucao")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formTravado) return;
                    setDigitsRaw((prev) => {
                      const d = computeNextDigits(prev.valorVenalConstrucao, e.target.value);
                      setForm((f) => ({ ...f, valorVenalConstrucao: formatMoneyFromDigits(d) }));
                      return { ...prev, valorVenalConstrucao: d };
                    });
                  }}
                />

                <TextField label="Valor Venal Construção (R$/m²)" value={form.valorVenalConstrucaoM2 ?? ""} disabled fullWidth size="small" sx={muiCampoSx} />
              </div>

              <div className="form-row">
                <TextField label="Valor Venal Imóvel" value={form.valorVenalImovel ?? ""} disabled fullWidth size="small" sx={muiCampoSx} />

                <TextField label="Imposto Predial" value={form.impostoPredial ?? ""} disabled fullWidth size="small" sx={muiCampoSx} />

                <TextField
                  label="Tx. Coleta de Lixo*"
                  value={form.taxaColetaLixo ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("taxaColetaLixo")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formTravado) return;
                    setDigitsRaw((prev) => {
                      const d = computeNextDigits(prev.taxaColetaLixo, e.target.value);
                      setForm((f) => ({ ...f, taxaColetaLixo: formatMoneyFromDigits(d) }));
                      return { ...prev, taxaColetaLixo: d };
                    });
                  }}
                />

                <TextField
                  label="Tx. Limpeza Pública*"
                  value={form.taxaLimpezaPublica ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("taxaLimpezaPublica")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formTravado) return;
                    setDigitsRaw((prev) => {
                      const d = computeNextDigits(prev.taxaLimpezaPublica, e.target.value);
                      setForm((f) => ({ ...f, taxaLimpezaPublica: formatMoneyFromDigits(d) }));
                      return { ...prev, taxaLimpezaPublica: d };
                    });
                  }}
                />
              </div>

              <div className="form-row">
                <TextField
                  label="Outras Adições*"
                  value={form.outrasAdicoes ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("outrasAdicoes")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formTravado) return;
                    setDigitsRaw((prev) => {
                      const d = computeNextDigits(prev.outrasAdicoes, e.target.value);
                      setForm((f) => ({ ...f, outrasAdicoes: formatMoneyFromDigits(d) }));
                      return { ...prev, outrasAdicoes: d };
                    });
                  }}
                />

                <TextField
                  label="Desconto IPTU Verde*"
                  value={form.descontoIptuVerde ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("descontoIptuVerde")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formTravado) return;
                    setDigitsRaw((prev) => {
                      const d = computeNextDigits(prev.descontoIptuVerde, e.target.value);
                      setForm((f) => ({ ...f, descontoIptuVerde: formatMoneyFromDigits(d) }));
                      return { ...prev, descontoIptuVerde: d };
                    });
                  }}
                />

                <TextField
                  label="Outros Descontos*"
                  value={form.outrosDescontos ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("outrosDescontos")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formTravado) return;
                    setDigitsRaw((prev) => {
                      const d = computeNextDigits(prev.outrosDescontos, e.target.value);
                      setForm((f) => ({ ...f, outrosDescontos: formatMoneyFromDigits(d) }));
                      return { ...prev, outrosDescontos: d };
                    });
                  }}
                />
              </div>

              <div className="form-row">
                <TextField label="Valor IPTU Ano" value={form.valorIptuAno ?? ""} disabled fullWidth size="small" sx={muiCampoSx} />

                <TextField
                  label="Valor IPTU Ano À Vista*"
                  value={form.valorIptuAnoAvista ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("valorIptuAnoAvista")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formTravado) return;
                    setDigitsRaw((prev) => {
                      const d = computeNextDigits(prev.valorIptuAnoAvista, e.target.value);
                      setForm((f) => ({ ...f, valorIptuAnoAvista: formatMoneyFromDigits(d) }));
                      return { ...prev, valorIptuAnoAvista: d };
                    });
                  }}
                />

                <TextField label="Desconto Pgto À Vista (%)" value={form.descontoPgtoAvista ?? ""} disabled fullWidth size="small" sx={muiCampoSx} />

                <TextField
                  select
                  label="Número de Parcelas*"
                  value={form.numeroParcelas ?? "12"}
                  disabled={formTravado}
                  error={erroCampoMUI("numeroParcelas")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  slotProps={{
                    select: menuPropsDark,
                  }}
                  onChange={(e) => {
                    if (formTravado) return;
                    setForm((f) => ({ ...f, numeroParcelas: e.target.value }));
                  }}
                >
                  {Array.from({ length: 12 }, (_, i) => String(i + 1)).map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              <div className="form-row">
                <TextField label="Valor IPTU Mês" value={form.valorIptuMes ?? ""} disabled fullWidth size="small" sx={muiCampoSx} />

                <TextField
                  label="Prazo de Responsabilidade*"
                  type="month"
                  value={form.prazoResponsabilidade ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("prazoResponsabilidade")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  onChange={(e) => {
                    if (formTravado) return;
                    setForm((f) => ({ ...f, prazoResponsabilidade: e.target.value }));
                  }}
                />
              </div>

              <div className="form-row">
                <TextField
                  label="Observações*"
                  value={form.observacoes ?? ""}
                  disabled={formTravado}
                  error={erroCampoMUI("observacoes")}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formTravado) return;

                    setForm((f) => ({
                      ...f,
                      observacoes: e.target.value.toUpperCase(),
                    }));
                  }}
                />
              </div>

              <div
                className="form-botoes"
                style={{
                  marginTop: 20,
                }}
              >
                {modoVisualizacao && !editandoIdForm && podeEditar && (
                  <button type="button" className="btn-cadastrar" onClick={entrarEdicao}>
                    <FaEdit /> Editar
                  </button>
                )}

                {(!modoVisualizacao || editandoIdForm) && (
                  <button type="submit" className="btn-cadastrar" style={{ flex: 1 }}>
                    <FaSave /> {editandoId ? "Atualizar" : "Cadastrar"}
                  </button>
                )}

                <button type="button" className="btn-acao" style={{ flex: 1 }} onClick={() => limparFormulario(anoSelecionado)}>
                  <FaTimes /> Cancelar
                </button>

                {podeMostrarExcluirNoForm && podeExcluir && (
                  <button type="button" className="btn-excluir" onClick={excluirRegistro}>
                    <FaTrash /> Excluir
                  </button>
                )}

                <button
                  type="button"
                  className={`btn-ged btn-ged-form ${temGedNoForm ? "btn-ged-ok" : ""}`}
                  style={{ flex: 1 }}
                  onClick={() => {
                    const input = document.getElementById("uploadGEDIptuAnual");
                    console.log("CLIQUE GED IPTU ANUAL:", input);
                    input?.click();
                  }}
                  disabled={false}
                >
                  <FaPaperclip className="iptu-btn-icone" />
                  GED
                  {(editandoId || idVisualizando) &&
                    (() => {
                      const reg = registros.find((r) => getRegistroId(r) === (editandoId || idVisualizando));
                      return reg?.qtdArquivos > 0 ? <span className="ged-badge">{reg.qtdArquivos}</span> : null;
                    })()}
                  {gedPendentes.length > 0 && !(editandoId || idVisualizando) && <span className="ged-badge">{gedPendentes.length}</span>}
                </button>
              </div>

              {/* LISTA GED (PENDENTES) */}
              {!formTravado && gedPendentes.length > 0 && (
                <div style={{ width: "100%", marginTop: "12px" }}>
                  <label style={{ display: "block", marginBottom: "8px" }}>Arquivos pendentes para anexar</label>

                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {gedPendentes.map((f, idx) => (
                      <div key={`${f.name}-${idx}`} style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                        <input value={f.name} readOnly className={classeCampoReadonly()} style={{ flex: 1 }} />

                        <button
                          type="button"
                          onClick={() => setGedPendentes((prev) => prev.filter((_, i) => i !== idx))}
                          style={{
                            background: "#c62828",
                            color: "#fff",
                            border: "none",
                            padding: "10px 12px",
                            borderRadius: "10px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            width: "110px",
                            height: "48px",
                          }}
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* LISTA GED (ANEXADOS) */}
              {(editandoId || idVisualizando) && arquivos.length > 0 && (
                <div style={{ width: "100%", marginTop: "12px" }}>
                  <label style={{ display: "block", marginBottom: "8px" }}>Arquivos anexados neste IPTU anual</label>

                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {arquivos.map((arq) => {
                      const arquivoId = arq.id || arq.nome;

                      return (
                        <div
                          key={arquivoId}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <input value={arq.nome} readOnly className={classeCampoReadonly()} style={{ flex: 1 }} />

                          <button
                            type="button"
                            className="btn-visualizar-ged"
                            onClick={() => visualizarArquivoGED(editandoId || idVisualizando, arquivoId, arq.nome)}
                          >
                            <FaEye /> Visualizar
                          </button>

                          <button type="button" className="btn-baixar-ged" onClick={() => baixarArquivoGED(editandoId || idVisualizando, arquivoId, arq.nome)}>
                            Baixar
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Input escondido para upload GED */}
              <input
                type="file"
                id="uploadGEDIptuAnual"
                multiple
                style={{ display: "none" }}
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  if (!files.length) return;

                  e.target.value = "";

                  const idAtual = editandoId || idVisualizando;

                  if (!idAtual) {
                    setGedPendentes((prev) => [...prev, ...files]);
                    alert(`Arquivos preparados (${files.length}). Clique em Cadastrar para anexar.`);
                    return;
                  }

                  const ok = await enviarGED(idAtual, files);

                  if (ok) {
                    alert("Arquivos enviados!");
                    await abrirGED(idAtual);
                    await carregarIPTUDoAno(anoSelecionado, true);
                  } else {
                    alert("Falha ao enviar arquivos.");
                  }
                }}
              />
            </form>
          </div>
        ) : (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">🔒 Você possui acesso apenas de visualização.</p>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}
